// The one offer to install, after a draft (INSTALL.md §4.3, §6 question 2;
// scenario M1), and inside the installed app the one offer of what the
// install was for: notifications on this device. B-243.
//
// HOW HARD WE PUSH IT, settled 2026-10-02 as my call (§6 question 2): a quiet,
// permanent *Install the app* in the account menu, and this card, once, on
// the League page of a league that has drafted — somebody who has sat through
// a draft feels the browser's chrome, somebody reading a page does not. Not a
// modal, never over the lineup, never mid-draft. "Not now" is said to the
// account, so it holds on every device, and the card may come back once after
// ninety days (`offerNow`, the account's rule). An install anywhere ends it.
//
// INSIDE THE INSTALLED APP the install is done, and the reason most people
// installed — on an iPhone, the only way to have notifications — is a tap
// away: the card offers push on this device until it is on, blocked, or
// waved off here. That "Not now" is this device's, because push is.

import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInstallState } from '../data/install';
import { pushState, turnOn } from '../data/push';
import { useSession } from '../data/session';
import { storageKey } from '../data/storage';
import { Panel } from '../kit/Panel';
import { Button, Text } from '../kit/ui';
import { isHandheld, isInstalled, thisPlatform } from './platform';
import '../styles/screens/account.css';

const PUSH_OFFER_CLOSED = storageKey('install.push-offer-closed.v1');

function pushOfferClosed(): boolean {
  try {
    return localStorage.getItem(PUSH_OFFER_CLOSED) !== null;
  } catch {
    return false;
  }
}

/**
 * The card, or null when there is none — a hook rather than a component, so
 * the page adds the card's slot only when there is a card to put in it: an
 * empty slot is a gap on every League page for everybody not being offered.
 * `active`: a real league that has drafted, signed in.
 */
export function useInstallOffer(active: boolean): ReactNode | null {
  const installed = isInstalled();
  // Both, every render, so the hooks inside run in the same order whichever
  // this device is; only one of them is ever active.
  const app = useAppOffer(active && !installed);
  const push = usePushOffer(active && installed);
  return installed ? push : app;
}

function useAppOffer(active: boolean): ReactNode | null {
  const navigate = useNavigate();
  const platform = thisPlatform();
  // Firefox on a computer cannot install a site; an offer it cannot take is
  // noise. The account menu's entry still explains where it can.
  const can = platform !== 'desktop-firefox';
  const { state, closeOffer } = useInstallState(active && can);
  if (!active || !can || state?.offerNow !== true) return null;
  const handheld = isHandheld(platform);
  return (
    <Panel
      title={handheld ? 'Put it on your home screen' : 'Install it as an app'}
      bodyClass="fsp-install-body"
    >
      <div className="fsp-install-offer">
        <img className="fsp-install-offer-icon" src="/icons/icon-192.png" alt="" />
        <div>
          <Text block>
            {handheld
              ? 'It opens full screen, like your other apps, and it can warn you before lineups lock.'
              : 'It opens in its own window, and it can warn you before lineups lock.'}
          </Text>
          <div className="fsp-install-offer-actions">
            <Button
              variant="primary"
              size="sm"
              icon="install"
              onClick={() => navigate('/account/install')}
            >
              Show me how
            </Button>
            <Button variant="quiet" size="sm" onClick={() => void closeOffer()}>
              Not now
            </Button>
          </div>
        </div>
      </div>
    </Panel>
  );
}

function usePushOffer(active: boolean): ReactNode | null {
  const { session } = useSession();
  const userId = session?.userId ?? null;
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    if (!active || userId === null || pushOfferClosed()) return;
    let live = true;
    void pushState(userId).then((s) => live && setShow(s === 'off'));
    return () => {
      live = false;
    };
  }, [active, userId]);
  if (!active || !show || userId === null) return null;
  const close = () => {
    try {
      localStorage.setItem(PUSH_OFFER_CLOSED, new Date().toISOString());
    } catch {
      // Storage blocked: the card comes back next time, which is the safe way to fail.
    }
    setShow(false);
  };
  return (
    <Panel title="Notifications on this device" bodyClass="fsp-install-body">
      <Text block>
        Get lineup warnings and the rest here, even with the app closed — the ones set to push in
        Settings. Your device will ask first.
      </Text>
      {problem !== null ? (
        <Text tone="bad" block>
          {problem}
        </Text>
      ) : null}
      <div className="fsp-install-offer-actions">
        <Button
          variant="primary"
          size="sm"
          icon="notifications"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void turnOn(userId).then((done) => {
              setBusy(false);
              if (!done.ok) setProblem(done.message);
              else if (done.state !== 'off') setShow(false);
            });
          }}
        >
          Turn on
        </Button>
        <Button variant="quiet" size="sm" onClick={close}>
          Not now
        </Button>
      </div>
    </Panel>
  );
}
